/**
 * DerivedAnthropometricsStrip.jsx — iter1001 (Feb 2026)
 *
 * Read-only info strip that surfaces the anthropometric metrics the BHI
 * pipeline already computes on the server (BMI · WHR · fat-mass estrogen
 * risk). Same numbers used by the personalized DIL calculator, so the
 * patient sees identical figures everywhere and never re-enters them.
 *
 * Rendered at the top of the risk-factor forms (ReferenceContext + Prevention
 * Path). Each tile carries a small "🧪 изчислено от антропометрия" tooltip
 * explaining where the value came from.
 */
import React from "react";
import { Info, Wand2 } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const CATEGORY_COLOR = {
  // BMI + WHR + estrogen buckets share the same green/amber/red spectrum.
  normal:       "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800/40",
  low:          "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800/40",
  underweight:  "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800/40",
  overweight:   "text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-950/40 dark:border-orange-800/40",
  moderate:     "text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-950/40 dark:border-orange-800/40",
  obese_1:      "text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-800/40",
  obese_2:      "text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-800/40",
  obese_3:      "text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-800/40",
  high:         "text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-800/40",
};

const TILES = [
  { key: "bmi", title: "Индекс на телесна маса (BMI)" },
  { key: "whr", title: "Талия / ханш (WHR)" },
  { key: "fat_mass_estrogen", title: "Мастна маса · естрогенен риск" },
];

export default function DerivedAnthropometricsStrip({ derived, isDark }) {
  if (!derived) return null;
  const anyData = TILES.some((t) => derived[t.key]?.has_data);
  if (!anyData) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-testid="derived-anthropometrics-strip"
        className={`rounded-2xl border p-3 mb-4 ${
          isDark ? "bg-slate-900/60 border-slate-800" : "bg-slate-50 border-slate-200"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <Wand2 className={`w-4 h-4 ${isDark ? "text-cyan-300" : "text-cyan-600"}`} />
          <div className={`text-xs font-bold ${isDark ? "text-cyan-200" : "text-cyan-800"}`}>
            Изчислено от антропометрия · авто-синхронизирано с DIL калкулатора
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {TILES.map(({ key, title }) => {
            const d = derived[key];
            if (!d?.has_data) {
              return (
                <div
                  key={key}
                  data-testid={`derived-tile-${key}-empty`}
                  className={`rounded-xl border p-3 text-[11px] ${
                    isDark ? "bg-slate-950/40 border-slate-800 text-slate-500"
                           : "bg-white border-slate-200 text-slate-500"
                  }`}
                >
                  <div className="font-semibold">{title}</div>
                  <div className="mt-1">Липсват данни — добави антропометрия в профила.</div>
                </div>
              );
            }
            const cls = CATEGORY_COLOR[d.category] || CATEGORY_COLOR.normal;
            return (
              <div
                key={key}
                data-testid={`derived-tile-${key}`}
                className={`rounded-xl border p-3 ${cls}`}
              >
                <div className="flex items-start gap-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
                      {title}
                    </div>
                    <div className="mt-1 text-lg font-bold leading-none">
                      {d.value}
                      {d.unit && (
                        <span className="ml-1 text-[11px] font-medium opacity-70">{d.unit}</span>
                      )}
                    </div>
                    <div className="mt-1 text-xs font-semibold">{d.label_bg}</div>
                    {d.note_bg && (
                      <div className="mt-1 text-[11px] leading-snug opacity-90">{d.note_bg}</div>
                    )}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="Как е изчислено"
                        data-testid={`derived-tile-${key}-info`}
                        className="p-0.5 -mr-1 -mt-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition"
                      >
                        <Info className="w-3.5 h-3.5 opacity-70" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px] text-xs">
                      {d.source_bg}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
